<template>
  <v-container
    class="weight--custom--container ml-1"
    v-closable="{ exclude: [customKey, `${customKey}_actions`, `${customKey}_text`], isClosable: true, handler: 'handleCancelEditing'}"
  >
    <v-row
      align="center"
      no-gutters
      style="z-index: 9;"
      :ref="customKey"
      v-on="listeners"
      @click="handleEditing"
      @mouseover="handleMouseOver"
      @mouseleave="handleMouseLeave"
    >
       <v-col
        cols="auto"
        class="mr-3"
       >
        <v-tooltip left>
          <template v-slot:activator="{ on }">
            <v-icon
            v-on="on"
            :size="$vuetify.breakpoint.lgAndDown ? '12px' : '14px'"
            >{{ icon }}</v-icon>
          </template>
          <span
            v-show="tooltip != null"
          >{{ tooltip }}
          </span>
        </v-tooltip>
       </v-col>
       <v-col
        cols="auto"
        class="mr-2"
        v-if="attribute != null"
       >
          <v-row
            no-gutters
            justify="start"
          >
            <span
              class="custom--text attribute"
              :class="[ $vuetify.breakpoint.lgAndDown ? 'font--size--xs' : 'font--size--lg' ]"
            >
              {{ attribute }}
            </span>
          </v-row>
       </v-col>
       <template
        v-if="isEditing"
       >
       <v-col
          cols="3"
          class="mr-2"
        >
          <v-input
            class="custom--input"
            :hide-details="true"
          >
            <v-text-field
              :ref="`${customKey}_first_input`"
              type="text"
              @keydown="inputKeyDownOnlyIntNumer"
              :label="label"
              outlined
              v-model="computedWeight"
              :hide-details="true"
              :height="$vuetify.breakpoint.lgAndDown ? '20px' : '24px'"
            >
            </v-text-field>
          </v-input>
        </v-col>
        <v-col
          cols="3"
        >
          <span
            class="message"
          >
            {{ message }}
          </span>
        </v-col>
       </template>
       <v-col
        v-show="!isEditing"
        :ref="`${customKey}_text`"
        cols="6"
       >
        <v-row
          no-gutters
          justify="start"
        >
        <span
          class="custom--text"
          :class="[ $vuetify.breakpoint.lgAndDown ? 'font--size--xs' : 'font--size--lg' ]"
        >
          {{ weight  }} kg
        </span>
        </v-row>
       </v-col>
       <v-col
        cols="5"
        class="text-right custom--actions"
        :class="{'visibility--hidden': !isHovered }"
        :ref="`${customKey}_actions`"
        v-if="!isEditing"
      >
          <v-tooltip top
          >
            <template v-slot:activator="{ on }">
              <v-icon
              v-on="on"
              :size="$vuetify.breakpoint.lgAndDown ? '14' : '16'"
              @click="handleEditing"
              class="edit"
              >
              fas fa-pen
              </v-icon>
            </template>
            <span>{{$t('buttons.edit')}}</span>
          </v-tooltip>
      </v-col>
        <v-col
          cols="5"
          class="text-right custom--actions"
          align-self="end"
          v-else
        >
          <v-tooltip top
          >
            <template v-slot:activator="{ on }">
              <v-icon
              v-if="isEditing"
              v-on="on"
              class="mr-2 save"
              :size="$vuetify.breakpoint.lgAndDown ? '14' : '16'"
              @click="handleSave"
              >
              fas fa-save
              </v-icon>
            </template>
            <span>{{$t('buttons.save')}}</span>
          </v-tooltip>
          <v-tooltip top
          >
            <template v-slot:activator="{ on }">
              <v-icon
              v-if="isEditing"
              v-on="on"
              :size="$vuetify.breakpoint.lgAndDown ? '14' : '16'"
              @click="handleCancelEditing"
              class="cancel"
              >
              fas fa-times
              </v-icon>
            </template>
            <span>{{$t('buttons.cancel')}}</span>
          </v-tooltip>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
  export default {
    name: 'ContextItemEditableWeight',
    props: {
      customKey: { type: String, required: true },
      icon: { type: String, default: 'fas fa-box-open' },
      tooltip: { type: String, default: null },
      weight: { type: Number | String, default: 0 },
      label: { type: String, default: null },
      savedByEnter: { type: Boolean, default: true },
      attribute: { type: String, default: null },
      forceIntInput: { type: Boolean, default: false }
    },
    data() {
      return {
        isEditing: false,
        isHovered: false,
        localWeight: null,
        message: null
      }
    },
    mounted() {
      this.localWeight = this.weight
    },
    methods: {
      handleCancelEditing () {
        this.isEditing = false
        this.isHovered = false
      },
      handleEditing () {
        this.isEditing = true
      },
      handleSave () {
        this.$emit('save', this.localWeight)
        setTimeout(() => {
          this.$nextTick(() => {
            this.handleCancelEditing()
          })
        }, 100)
      },
      handleMouseOver () {
        this.isHovered = true
      },
      handleMouseLeave () {
        if (!this.isEditing) {
          this.isHovered = false
        }
      },
      inputKeyDownOnlyIntNumer(event) {
        this.message = null
        if (this.forceIntInput) {
          if (event.keyCode == 8 || event.keyCode == 46 || event.keyCode == 37 || event.keyCode == 39 || event.keyCode == 9 || event.keyCode == 13) {
            return
          } else {
            const char = String.fromCharCode(event.keyCode)
            if (!/[0-9]/.test(char)) {
              this.message = this.$t('validations.integer_validation')
              event.preventDefault()
            }
          }
        }
      }
    },
    computed: {
      listeners () {
        if (this.savedByEnter) {
          return { keyup: (event) => {
            if (event.key === 'Enter') {
              this.handleSave()
            }
          } }
        } else {
          return {}
        }
      },
      computedWeight: {
        get () {
          return this.weight
        },
        set (value) {
          this.localWeight = value
          this.$emit('update:prop', value)
        }
      },
    },
    beforeDestroy() {
      this.isHovered = false
      this.isEditing = false
    },
    watch: {
      isEditing: {
        immediate: true,
        handler(val, oldVal) {
          if (val && !oldVal) {
            this.$nextTick(() => {
              this.$refs[`${this.customKey}_first_input`].focus()
            })
          }
        }
      }
    },
  }
</script>

<style lang="scss">
  .weight--custom--container {
    border-radius: 5px;
    margin-left: -3px;
    padding: 3px 3px 3px 3px !important;
    position: relative;
    &.editing {
      border: 2px solid var(--v-wrld_black-base)
    }
    &:hover {
      background-color: #f2f2f2;
    }
    .custom--text {
      color: var(--v-wrld_subtitle-base);
      font-weight: 500;
      &.attribute {
        color: var(--v-wrld_title-base);
      }
      &.font--size--lg {
        font-size: 14px !important;
      }
      &.font--size--xs {
        font-size: 12px !important;
      }
    }
    .message {
      font-size: 12px;
      color: #f44336;
    }
    .custom--input {
      .v-input__append-inner {
        display: none !important;
      }
      .v-text-field .v-input__control .v-input__slot {
        font-size: 0.8em;
        min-height: auto !important;
        display: flex !important;
        align-items: center !important;
      }
    }
    .custom--actions {
      .save {
        position: absolute;
        right: 2%;
        top: calc(50% - 7px);
      }
      .cancel {
        position: absolute;
        right: 7%;
        top: calc(50% - 7px);
      }
      .edit {
        position: absolute;
        right: 2%;
        top: calc(50% - 7px);
      }
    }
    /* Chrome, Safari, Edge, Opera */
    input::-webkit-outer-spin-button,
    input::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
    /* Firefox */
    input[type=number] {
      -moz-appearance: textfield;
    }
  }
</style>
